import { TextSelection, NodeSelection, Plugin, PluginKey } from 'prosemirror-state';
import { liftListItem as liftListItem$1, sinkListItem } from 'prosemirror-schema-list';
import { NodeExtension } from '../../../core/index.js';
import { liftTarget, ReplaceAroundStep, canSplit } from 'prosemirror-transform';
import { v4 } from 'uuid';
import 'prosemirror-commands';
import 'prosemirror-inputrules';
import { Slice, Fragment, NodeRange } from 'prosemirror-model';
import { GapCursor, gapCursorSituation } from '../../../citadel/kit/GapcursorSelection/index.js';

/**
 * 统一样式前缀
 */ var CLASSNAME_PREFIX = "ct";

var classNamePrefix = function(className) {
    var prefix = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : CLASSNAME_PREFIX;
    return "".concat(prefix, "-").concat(className);
};

var findParentNodeClosestToPos = function($pos, predicate) {
    for(var i = $pos.depth; i > 0; i--){
        var node = $pos.node(i);
        if (predicate(node)) {
            return {
                pos: i > 0 ? $pos.before(i) : 0,
                start: $pos.start(i),
                depth: i,
                node: node
            };
        }
    }
};
var findParentNode = function(predicate) {
    return function(param) {
        var $from = param.$from;
        return findParentNodeClosestToPos($from, predicate);
    };
};
// 类型是否可以插入富文本
var canInsertTypes = function(state) {
    var parentNode = state.selection.$from.parent;
    var textType = [
        "status",
        "code_block",
        "note_title",
        "collapse_title"
    ];
    return !textType.includes(parentNode.type.name);
};

function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
var listTypes = [
    "bullet_list",
    "ordered_list",
    "task_list"
];
// 获取列表项结尾到下一行开头的 pos 距离
function distanceFromItemEndToNextlineStart(listNode) {
    var dis = 4; // 空的一级列表到下一行开头的距离为 4
    var curNode = listNode;
    // 列表每增加一级，距离加 2
    while(true){
        curNode = curNode.lastChild.lastChild;
        if (!listTypes.includes(curNode.type.name)) break;
        dis += 2;
    }
    if (![
        "paragraph",
        "heading"
    ].includes(curNode.type.name)) dis -= 1;
    return dis;
}
function liftToOuterList(tr, itemType, range) {
    var transaction = tr;
    var end = range.end;
    var endOfList = range.$to.end(range.depth);
    if (end < endOfList) {
        // There are siblings after the lifted items, which must become
        // children of the last item
        transaction.step(new ReplaceAroundStep(end - 1, endOfList, end, endOfList, new Slice(Fragment.from(itemType.create(null, range.parent.copy())), 1, 0), 1, true));
        range = new NodeRange(transaction.doc.resolve(range.$from.pos), transaction.doc.resolve(endOfList), range.depth);
    }
    return transaction.lift(range, liftTarget(range));
}
function liftOutOfList(tr, range) {
    var transaction = tr;
    var list = range.parent;
    // Merge the list items into a single big item
    for(var pos = range.end, i = range.endIndex - 1, e = range.startIndex; i > e; i--){
        pos -= list.child(i).nodeSize;
        transaction.delete(pos - 1, pos + 1);
    }
    var $start = transaction.doc.resolve(range.start);
    var item = $start.nodeAfter;
    var atStart = range.startIndex === 0;
    var atEnd = range.endIndex === list.childCount;
    var parent = $start.node(-1);
    var indexBefore = $start.index(-1);
    if (!parent.canReplace(indexBefore + (atStart ? 0 : 1), indexBefore + 1, item.content.append(atEnd ? Fragment.empty : Fragment.from(list)))) {
        return false;
    }
    var start = $start.pos;
    var end = start + item.nodeSize;
    // 单独处理 data-diff-id 问题，后半段的 list 认为是新的 list，data-diff-id 重新生成
    // 详见 https://ones.sankuai.com/ones/product/1859/workItem/defect/detail/60669246
    var newList = list.copy(Fragment.empty);
    newList.attrs = _objectSpread({}, newList.attrs);
    newList.attrs.dataDiffId = "ct-diff-id-".concat(v4());
    // Strip off the surrounding list. At the sides where we're not at
    // the end of the list, the existing list is closed. At sides where
    // this is the end, it is overwritten to its end.
    transaction.step(new ReplaceAroundStep(start - (atStart ? 1 : 0), end + (atEnd ? 1 : 0), start + 1, end - 1, new Slice((atStart ? Fragment.empty : Fragment.from(list.copy(Fragment.empty))).append(atEnd ? Fragment.empty : Fragment.from(newList)), atStart ? 0 : 1, atEnd ? 0 : 1), atStart ? 0 : 1));
    return transaction;
}
// :: (NodeType) → (state: EditorState, dispatch: ?(tr: Transaction)) → bool
// Create a command to lift the list item around the selection up into
// a wrapping list.
function liftListItem(itemType) {
    return function(tr, selRange) {
        var transaction = tr;
        var $from = selRange.$from, $to = selRange.$to;
        var range = $from.blockRange($to, function(node) {
            return node.childCount && node.firstChild.type === itemType;
        });
        if (!range) return false;
        if ($from.node(range.depth - 1).type === itemType) {
            // Inside a parent list
            return liftToOuterList(transaction, itemType, range);
        }
        // Outer list node
        return liftOutOfList(transaction, range);
    };
}

function _arrayLikeToArray(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithoutHoles(arr) {
    if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _defineProperty$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _iterableToArray(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _nonIterableSpread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _objectSpread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$1(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _toConsumableArray(arr) {
    return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _unsupportedIterableToArray(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
var splitListItem = function(itemType) {
    var provider = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : undefined;
    return function(state, dispatch) {
        var tr = state.tr;
        var selection = state.selection;
        // 光标在inline节点末尾（如链接），纠正光标的位置
        var $anchor = selection.$anchor, from = selection.from;
        if (!$anchor.parent.type.isBlock && !$anchor.nodeAfter) {
            var predicate = function(n) {
                return n.type && [
                    "paragraph",
                    "heading"
                ].includes(n.type.name);
            };
            var node = findParentNode(predicate)(selection);
            if (!node) return false;
            var pos = node.pos + node.node.nodeSize - 1;
            selection = TextSelection.create(state.doc, pos);
            tr.setSelection(selection);
        }
        var $from = selection.$from, $to = selection.$to;
        if (!canInsertTypes(state)) return false;
        if (_instanceof(selection, NodeSelection) && selection.node.isBlock || // 当前光标在块节点后
        $from.depth < 2 || // 当前光标所在深度小于2，说明一定不再列表项里
        !$from.sameParent($to) // 跨节点选区
        ) {
            return false;
        }
        var itemNode = $from.node(-1);
        // itemNode.type 存在 list_item、task_item 两种情况
        if (itemNode.type !== itemType) return false;
        // 列表项首个子节点为空（一个列表项可能有多个 paragraph），将下方的列表项提升或脱离
        if ($from.parent === itemNode.firstChild && $from.parent.content.size === 0) {
            liftListItem(itemType)(tr, {
                $from: $from,
                $to: $to
            });
            dispatch(tr);
            return true;
        }
        var nextType = $to.pos === $from.end() ? itemNode.contentMatchAt(0).defaultType : null;
        tr.delete($from.pos, $to.pos);
        var types = nextType && [
            null,
            {
                type: nextType,
                attrs: _objectSpread$1({}, $from.node().attrs)
            }
        ];
        // TODO：暂时没有找到不能 split 的例子，单测待补充
        if (!canSplit(tr.doc, $from.pos, 2, types)) return false;
        var atStart = itemNode.firstChild === $to.node() && $to.parentOffset === 0;
        var _state_schema = state.schema, _state_schema_nodes = _state_schema.nodes, task_item = _state_schema_nodes.task_item, list_item = _state_schema_nodes.list_item, paragraph = _state_schema_nodes.paragraph;
        if (atStart) {
            // 在开头回车 - 等同于将下一行原封不动(diff-id等属性)下移一行
            var pos1 = findParentNode(function(n) {
                return [
                    task_item,
                    list_item
                ].includes(n.type);
            })(selection).pos;
            tr.insert(pos1, itemNode.type.createAndFill({
                dataListItemDiffId: "ct-list-item-diff-id-".concat(v4())
            }));
            dispatch(tr.scrollIntoView());
            return true;
        }
        // 任务列表特殊处理
        if (itemNode.type === task_item) {
            // 任务列表要考虑checked属性
            var pos2 = findParentNode(function(n) {
                return n.type === task_item;
            })(selection).pos;
            tr.setNodeMarkup(pos2, null, _objectSpreadProps(_objectSpread$1({}, itemNode.attrs), {
                dataListItemDiffId: "ct-list-item-diff-id-".concat(v4())
            }));
            var task_item_type = {
                type: task_item,
                attrs: _objectSpreadProps(_objectSpread$1({}, $from.node().attrs), {
                    checked: false
                })
            };
            var typesAfter = [
                task_item_type
            ];
            if (types) {
                typesAfter = [
                    task_item_type,
                    types[1]
                ];
            }
            tr.split($from.pos, 2, typesAfter);
        } else {
            // 否则按照新diff-id的原则split
            tr.split(tr.mapping.map($from.pos), 2, [
                {
                    type: list_item,
                    attrs: {
                        dataListItemDiffId: "ct-list-item-diff-id-".concat(v4())
                    }
                },
                {
                    type: paragraph,
                    attrs: {
                        dataDiffId: "ct-diff-id-".concat(v4())
                    }
                }
            ]);
        }
        var marks = state.storedMarks || $to.parentOffset && $from.marks() || [];
        // 沿用当前节点的 mark
        if (marks) {
            if (provider) {
                // 下面的处理是为了支持通过配置去除切分块级元素时不需要保留的Mark，例如划词评论的引用
                var newMarks = _toConsumableArray(marks);
                newMarks.forEach(function(mark, index) {
                    var _provider_excludeMarkNames, _provider_excludeMarkNames1;
                    if (provider.excludeMarkNames && ((_provider_excludeMarkNames = provider.excludeMarkNames) === null || _provider_excludeMarkNames === void 0 ? void 0 : _provider_excludeMarkNames.length) > 0 && ((_provider_excludeMarkNames1 = provider.excludeMarkNames) === null || _provider_excludeMarkNames1 === void 0 ? void 0 : _provider_excludeMarkNames1.indexOf(mark.type.name)) !== -1) {
                        newMarks.splice(index, 1);
                    }
                    tr.ensureMarks(newMarks);
                });
            } else {
                tr.ensureMarks(marks);
            }
        }
        dispatch(tr.scrollIntoView());
        return true;
    };
};

function _instanceof$1(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
var isGapCursorBeforeNode = function(selection) {
    if (_instanceof$1(selection, GapCursor)) {
        return [
            gapCursorSituation.beforeNode
        ].includes(selection.gapCursorType);
    }
    return false;
};
var backspaceInList = function(itemType) {
    return function(state, dispatch) {
        var tr = state.tr, selection = state.selection, schema = state.schema;
        var _schema_nodes = schema.nodes, list_item = _schema_nodes.list_item, task_item = _schema_nodes.task_item, bullet_list = _schema_nodes.bullet_list, ordered_list = _schema_nodes.ordered_list, task_list = _schema_nodes.task_list, doc = _schema_nodes.doc;
        var itemTypes = [
            list_item,
            task_item
        ];
        var listTypes = [
            bullet_list,
            ordered_list,
            task_list
        ];
        var $from = selection.$from, from = selection.from, $to = selection.$to, to = selection.to;
        var $cursor = _instanceof$1(selection, TextSelection) ? selection.$cursor : _instanceof$1(selection, GapCursor) ? selection.$head : null;
        // 光标在行首且单光标
        if ($from.parentOffset === 0) {
            var fromNodeParent = $from.node(-1);
            // 此时如果是 gapcursor，且前面没有节点，说明在文档首位
            if (!fromNodeParent) return false;
            if ($cursor) {
                // 如果光标在行内模块中（如链接前），向前移1个位置
                if ($cursor.parent.isInline) $cursor = tr.doc.resolve($cursor.pos - 1);
                var cursorNode = isGapCursorBeforeNode(selection) ? selection.targetNode : $cursor.node();
                var cursorNodeParent = isGapCursorBeforeNode(selection) ? $cursor.node() : $cursor.node(-1);
                // 光标在列表项的首个子节点中（一个列表项可能有多个 paragraph，如果不是首个子节点则走默认的Backspace事件）
                if (itemTypes.includes(cursorNodeParent.type) && cursorNode === cursorNodeParent.firstChild) {
                    /** 获取以当前项为一级的整个列表节点，如：
         *  1. 111
         *    a. 222
         *    b. 333
         *      i. 444
         * 光标在 "b. " 后时，获取的 listNode 内容包含：222333444
         */ var range = $cursor.blockRange($cursor, function(node) {
                        return node.childCount && node.firstChild.type === cursorNodeParent.type;
                    });
                    if (!range) return false; // TODO：暂时没有找到 range 非法的例子，单测待补充
                    var listNode = range.parent;
                    // 不是 listNode 的首个子列表项
                    if (listNode.content.firstChild !== cursorNodeParent) {
                        var _$cursor_nodeAfter;
                        var isAfterBlock = (_$cursor_nodeAfter = $cursor.nodeAfter) === null || _$cursor_nodeAfter === void 0 ? void 0 : _$cursor_nodeAfter.isBlock;
                        // block 前要多删除 p 标签
                        var offsetStartPos = isAfterBlock ? 2 : 3;
                        tr.delete($cursor.pos - offsetStartPos, $cursor.pos);
                        dispatch(tr);
                        return true;
                    }
                    // 当前的列表类型与调用command的extension类型不匹配时，不执行liftListItem
                    if (listNode.type === task_list && itemType === list_item || listNode.type !== task_list && itemType === task_item) {
                        return false;
                    }
                    // 计算 listNode 后面是否还有元素
                    var $end = tr.doc.resolve(range.end);
                    var listRange = $end.blockRange($end);
                    var end = listRange.end;
                    var endOfList = listRange.$to.end(listRange.depth);
                    var isHaveNodeAfterListNode = end < endOfList;
                    var result = liftListItem(itemType)(tr, selection);
                    // fix ones#68740873 【学城2.0】删除完一个待办事项（任务框），出现两个
                    // 执行了 liftListItem 且 listNode 后面有元素，说明后面元素需要删掉 list_item/task_item
                    if (result && itemTypes.includes(listRange.parent.type) && isHaveNodeAfterListNode) {
                        var mappedEnd = tr.mapping.map(listRange.end);
                        tr.delete(mappedEnd - 2, mappedEnd);
                    }
                    dispatch(tr);
                    return !!result;
                }
                // 光标上一行的 node，如果光标在文档首位则为 null
                // 光标不在文档首位，且$cursor为TextSelection，$cursor.pos >= 1
                var preRowNode = $cursor.pos === 1 ? null : tr.doc.resolve($cursor.pos - 2).parent;
                // 上一行是列表
                if (preRowNode && listTypes.includes(preRowNode.type)) {
                    var distance = distanceFromItemEndToNextlineStart(preRowNode);
                    var newPos = $cursor.pos - distance; // 上一行列表项末尾的位置
                    // 如果光标后没有内容，则直接删除该行，否则将后面内容拼接到上一行的列表项后
                    var lineStartPos = $cursor.pos - 1;
                    if (!$to.nodeAfter) {
                        tr.delete(lineStartPos, to + 1);
                        tr.setSelection(TextSelection.create(tr.doc, newPos));
                    } else {
                        var cursorNodeSize = cursorNode.nodeSize;
                        // 特殊处理当前节点是父节点是 doc 的最后一个节点时，多保留一个空行
                        if (cursorNodeParent.lastChild === cursorNode && cursorNodeParent.type === doc) {
                            tr.delete(lineStartPos, lineStartPos + cursorNodeSize - 1);
                        } else {
                            tr.delete(lineStartPos, lineStartPos + cursorNodeSize);
                        }
                        tr.insert(newPos, cursorNode.content);
                        tr.setSelection(TextSelection.create(tr.doc, newPos));
                    }
                    dispatch(tr);
                    return true;
                }
                return false;
            }
        }
        // 找到 from to 所属的 listNode
        var predicate = function(n) {
            return n.type && listTypes.includes(n.type);
        };
        var fromNode = findParentNode(predicate)(selection);
        var toNode = findParentNodeClosestToPos($to, predicate);
        // 跨节点删除，且 $to 所在的列表项完全删除（之后无内容需要与前面拼接）
        if (toNode && (!fromNode || fromNode.node !== toNode.node) && $to.parentOffset === $to.node().content.size) {
            /**
     * 以 <ul><li><p>123</p><ul><li><p>456</p><ul><li><p>789</p></li></ul></li></ul></li></ul> 为例
     * 如果 to 在 6 之后，则会删除 </p><ul><li>
     * 如果 to 在 9 之后，则会删除 </p></li></ul>
     */ tr.delete(from, to + 3);
            tr.setSelection(TextSelection.create(tr.doc, from));
            dispatch(tr);
            return true;
        }
        /**
   * fix: ONES #67985267 如果from位置在列表里，则执行自定义的删除逻辑 (缺点：cut的时候还是会跳动)
   * fix: ONES #69076581 【2.0】列表项中的表格无法删除，预期是可以正常删除
   * @see https://km.sankuai.com/collabpage/1336274120
   */ var needCustomDelSel = !!fromNode && _instanceof$1(selection, TextSelection);
        if (needCustomDelSel) {
            if (state.selection.empty) return false;
            if (dispatch) {
                tr.delete(from, to);
                tr.scrollIntoView();
                dispatch(tr);
                return true;
            }
        }
        return false;
    };
};

var convertCommand = function(commandFunction) {
    return function(param) {
        var state = param.state, dispatch = param.dispatch, view = param.view, args = param.args;
        return commandFunction(state, dispatch, view, args);
    };
};

function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
var ListItemNodeView = /*#__PURE__*/ function() {
    function ListItemNodeView(node) {
        _classCallCheck(this, ListItemNodeView);
        this.dom = document.createElement("li");
        this.dom = document.createElement("li");
        this.addClass(node);
        this.contentDOM = this.dom;
    }
    var _proto = ListItemNodeView.prototype;
    _proto.update = function update(node) {
        this.addClass(node);
        return true;
    };
    _proto.addClass = function addClass(node) {
        var firstChild = node.firstChild, _node_attrs = node.attrs, dataListItemDiffId = _node_attrs.dataListItemDiffId, hidden = _node_attrs.hidden;
        var className = hidden ? "ct-hidden-item" : "";
        if (firstChild && firstChild.type.name === "heading") {
            this.dom.className = "".concat(className, " ").concat(classNamePrefix("item-level-".concat(firstChild.attrs.level)));
        } else {
            this.dom.className = className;
        }
        this.dom.setAttribute("data-list-item-diff-id", dataListItemDiffId);
    };
    return ListItemNodeView;
}();

function styleInject(css, ref) {
    if (ref === void 0) ref = {};
    var insertAt = ref.insertAt;
    if (!css || typeof document === "undefined") {
        return;
    }
    var head = document.head || document.getElementsByTagName("head")[0];
    var style = document.createElement("style");
    style.type = "text/css";
    if (insertAt === "top") {
        if (head.firstChild) {
            head.insertBefore(style, head.firstChild);
        } else {
            head.appendChild(style);
        }
    } else {
        head.appendChild(style);
    }
    if (style.styleSheet) {
        style.styleSheet.cssText = css;
    } else {
        style.appendChild(document.createTextNode(css));
    }
}

var css_248z = "li.ProseMirror-selectednode:after{display:none}.ct-hidden-item{list-style:none}";
styleInject(css_248z);

function _assertThisInitialized(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck$1(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
function _getPrototypeOf(o) {
    _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf(o);
}
function _inherits(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf(subClass, superClass);
}
function _possibleConstructorReturn(self, call) {
    if (call && (_typeof(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized(self);
}
function _setPrototypeOf(o, p) {
    _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf(o, p);
}
var _typeof = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn(this, result);
    };
}
var ListItemExtension = /*#__PURE__*/ function(NodeExtension) {
    _inherits(ListItemExtension, NodeExtension);
    var _super = _createSuper(ListItemExtension);
    function ListItemExtension() {
        _classCallCheck$1(this, ListItemExtension);
        return _super.apply(this, arguments);
    }
    var _proto = ListItemExtension.prototype;
    _proto.plugin = function plugin() {
        return new Plugin({
            key: new PluginKey(this.name),
            props: {
                nodeViews: {
                    list_item: function(node) {
                        return new ListItemNodeView(node);
                    }
                }
            }
        });
    };
    _proto.keys = function keys(param) {
        var type = param.type, providers = param.providers;
        var listItemProvider = providers.get(this.name);
        return {
            Enter: convertCommand(splitListItem(type, listItemProvider)),
            Backspace: convertCommand(backspaceInList(type))
        };
    };
    _proto.commands = function commands(param) {
        var type = param.type;
        return {
            liftListItem: function() {
                return liftListItem$1(type);
            },
            sinkListItem: function() {
                return sinkListItem(type);
            }
        };
    };
    _createClass(ListItemExtension, [
        {
            key: "name",
            get: function get() {
                return "list_item";
            }
        }
    ]);
    return ListItemExtension;
}(NodeExtension);

export { ListItemExtension };
//# sourceMappingURL=index.js.map
